import { buildQuery } from "@/shared/utils/buildQuery";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { addDays, format, subDays } from "date-fns";
import { useMemo, useState } from "react";
import { ItqanClient, NTaskClient, TasksByDate } from "../types/ClientTasksBoardTasks";
import useQueries from "./useQueries";
//"48827011704062026638"
const fetchItqanClients = async (ItqanAccountsQuery: string) => {
    const configRes = await fetch('/config.json');
    const config = await configRes.json();
    const licenceId = config?.licenceId;
    const sessionId = config?.sessionId;

    const response = await axios.post(config?.Itqan, {
        q: ItqanAccountsQuery,
    }, {
        params: { t: 'sel', phone: "", id: licenceId, s: sessionId, btn: 'itq' }
    });
    return response?.data?.data?.result ?? [];
}

const fetchNTasksClients = async () => {
    const configRes = await fetch('/config.json');
    const config = await configRes.json();
    const response = await axios.get(config.nTask);
    return response?.data?.data ?? [];
}


const useClientTasksSyncBoard = () => {

    const queries = useQueries();

    const ItqanAccountsQuery = queries ? buildQuery(queries?.ItqanAccountsQuery) : '';


    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean; type: 'multi' | 'empty' | 'only_in_itqan' | ''
    }>({
        isOpen: false,
        type: ''
    });
    const [currentWeekStart, setCurrentWeekStart] = useState(subDays(new Date(), 1));
    const [isMerged, setIsMerged] = useState(localStorage.getItem('isMerged') === 'true');

    const nextWeek = () => setCurrentWeekStart(prev => addDays(prev, 7));
    const prevWeek = () => setCurrentWeekStart(prev => subDays(prev, 7));

    const weekDays = useMemo(() => {
        return [...Array(7)].map((_, i) => format(addDays(currentWeekStart, i), "yyyy-MM-dd"))
    }, [currentWeekStart]);

    const { data: nTasksRaw, isLoading } = useQuery<NTaskClient[]>(
        {
            queryKey: ['nTasksClients'],
            queryFn: fetchNTasksClients,
            refetchInterval: 60000,
            refetchOnWindowFocus: true,
            retry: 3
        }
    );

    const { data: itqanRaw } = useQuery<ItqanClient[]>(
        {
            queryKey: ['itqanClients', ItqanAccountsQuery],
            queryFn: () => fetchItqanClients(ItqanAccountsQuery),
            refetchInterval: 60000,
            refetchOnWindowFocus: true,
        }
    );




    const processed = useMemo(() => {
        if (!nTasksRaw || !itqanRaw) return {
            weekDays,
            matchedTasks: {},
            unmatchedTasks: {},
            matched: [],
            unmatched: [],
            onlyInItqan: []
        };

        const itqanMap = new Map(itqanRaw.map(i => [String(i.account_id), i]));

        const matchedItqanIds = new Set<string>();

        const matched = [];
        const unmatched = [];

        const matchedTasks: TasksByDate = {};
        const unmatchedTasks: TasksByDate = {};


        weekDays.forEach(date => {
            matchedTasks[date] = [];
            unmatchedTasks[date] = []
        });

        nTasksRaw.forEach(client => {

            const accountId = client?.value && String(client?.value)?.trim() !== ''
                ? String(client?.value)
                : null;

            const itqanData = accountId ? itqanMap.get(accountId) : undefined;

            const fullClient = { ...client, itqan_data: itqanData };

            const targetTasks = itqanData ? matchedTasks : unmatchedTasks;

            if (itqanData) {
                matched.push(fullClient);
                matchedItqanIds.add(accountId);
            }
            else unmatched.push(fullClient);


            client.tasks?.forEach(task => {
                const dateKey = task.date.split(' ')[0];
                if (targetTasks[dateKey]) {
                    targetTasks[dateKey].push({
                        clientName: client.client_name,
                        taskTitle: task.title,
                        status: task.status,
                        color: task.color || '#FFFFFF'
                    });
                }
            });
        });



        const onlyInItqan = itqanRaw?.filter(item => !matchedItqanIds.has(String(item?.account_id)))


        return {
            weekDays,
            matchedTasks,
            unmatchedTasks,
            matched,
            unmatched,
            onlyInItqan
        };
    }, [nTasksRaw, itqanRaw, weekDays])



    const closeDialog = () => setModalConfig({ isOpen: false, type: '' });

    const filteredClientsForModal = useMemo(() => {
        if (!modalConfig.type) return [];
        if (modalConfig.type == 'multi') {
            return processed?.matched?.filter(client => client.tasks.length > 1);
        }
        if (modalConfig.type == 'empty') {
            return processed.matched?.filter(client => client?.tasks?.length === 0);
        }
        if (modalConfig.type === 'only_in_itqan') {
            return processed.onlyInItqan;
        }
        return [];

    }, [processed?.matched, processed.onlyInItqan, modalConfig.type]);




    // first table client value in nTasks = account_id in itqan and  tasks.length > 0
    // second table is clients in nTask does not have value but tasks.length > 0 
    // first button clients with value and account id but tasks.length == 0
    // second button just inside itqan but not matching in nTask
    // third button clients with value and account id but tasks.length > 1



    return {
        processed,
        filteredClientsForModal,
        modalConfig,
        isLoading, isMerged,
        setIsMerged,
        setModalConfig,
        closeDialog,
        nextWeek,
        prevWeek
    }
}

export default useClientTasksSyncBoard;